import api from '@sgp/config/api-urls';
import { createFetcher } from '@lib/fetch';
import getHeaders from '@sgp/lib/util/header';
import useApiConfig from '@shared/hooks/useApiConfig';
import { sessionClear } from '@sgp/lib/util/utilities';
import { getHeadersForAuthCode } from '@sgp/lib/util/env-urls';
import { requestCidToken, requestTrustToken, getUserEmail } from '@sgp/cognito';
import { isSsoSessionActive, toSsoUrl, getSsoUserEmail } from '@sgp/sso';
import { toAgentList, selectAgent, mapOdsAgentToUserProfile } from './odsAgent';

// entity specific headers
const STATIC_HEADERS = getHeaders;
const rawFetch = createFetcher(STATIC_HEADERS());

// Backend-auth token mode, chosen at build time via the AUTH_TOKEN_MODE DefinePlugin
// constant (webpack.config.js): 'cid' = eB2B CID token (default), 'ib2b' = iB2B OBO
// trust token. Kept as a switch so the two flows can be toggled per build/env without
// touching call sites.
const AUTH_MODE = (typeof AUTH_TOKEN_MODE !== 'undefined' && AUTH_TOKEN_MODE) || 'cid';
const IS_IB2B = AUTH_MODE === 'ib2b';

// eB2B CID-Token pattern: every protected backend request carries a CID token in the
// `Authorization: JWS <token>` header (the CID token binds to the request payload hash).
// iB2B OBO pattern: the request carries an `X-HSBC-E2E-Trust-Token` instead (no payload
// binding). In both cases the BFF validates the Cognito access_token before issuing the
// token; Kong forwards/translates it into an X-HSBC-E2E-Trust-Token for the backend.
// See src/client/cognito/{cidToken,trustToken}.js and the md-files security docs.
//
// Attach the configured backend-auth token to the outgoing request headers, returning a
// new headers object (or the originals unchanged if issuing the token fails, so the
// gateway's 401 drives the existing re-login handling).
const attachAuthToken = async (headers, payload) => {
  try {
    if (IS_IB2B) {
      const trustToken = await requestTrustToken();
      return { ...headers, 'X-HSBC-E2E-Trust-Token': trustToken };
    }
    const cidToken = await requestCidToken(payload);
    // Replace the Cognito Bearer token with the minted CID token (JWS scheme).
    return { ...headers, Authorization: `JWS ${cidToken}` };
  } catch (error) {
    console.error(
      `[auth token] Unable to attach ${IS_IB2B ? 'trust' : 'CID'} token:`,
      error && error.message
    );
    return headers;
  }
};

const fetch = async (request = {}) => {
  const { payload = {}, headers = {}, skipCidToken = false, skipSsoUserEmail = false, url, ...rest } = request;
  let requestHeaders = headers;

  // SSO (DSP) users reach the backend through a separate absolute "internal-proxy"
  // gateway and authenticate with the DSP session cookie (sent automatically by the
  // fetch layer via credentials:'include'). They have no Cognito token, so for the
  // SSO path we both (a) skip the CID/trust token and (b) rewrite the URL to the SSO
  // internal-proxy host. The Cognito path is left completely unchanged.
  const ssoActive = isSsoSessionActive();

  // Pre-auth calls (auth-code / token exchange) run before a Cognito token exists and
  // must not carry a backend-auth token; they opt out via skipCidToken.
  if (!skipCidToken && !ssoActive) {
    requestHeaders = await attachAuthToken(headers, payload);
  }

  // The backend resolves the ODS agent profile by user email. In the Cognito flow the
  // email comes from the Cognito session (getHeaders); the SSO flow has no Cognito
  // session, so forward the DSP-resolved email instead. Without it get_user_profile
  // cannot identify the agent and fails, which the 401 handler turns into a /logout.
  // Callers that pass the email another way opt out via skipSsoUserEmail: the SSO internal-proxy does not list X-HSBC-User-Email in its CORS allow-headers, so sending it makes the browser reject the preflight.
  if (ssoActive && !skipSsoUserEmail) {
    const ssoEmail = getSsoUserEmail();
    if (ssoEmail) {
      requestHeaders = { ...requestHeaders, 'X-HSBC-User-Email': ssoEmail };
    }
  }

  const requestUrl = ssoActive ? toSsoUrl(url) : url;

  return rawFetch({ ...rest, url: requestUrl, payload, headers: requestHeaders });
};

//below is temporary for unit testing
let API_CONFIG;
try {
  API_CONFIG = useApiConfig(api);
} catch (e) {
  API_CONFIG = api;
}

const parseJsonSafe = async (response) => {
  let text = '';
  try {
    text = await response.text();
  } catch (e) {
    return {};
  }

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch (e) {
    return {};
  }
};

// Method to validate 400 and  401 error message for DSP Flow and Normal API Flow
const validateErrorMessage = (status, errorBody) => {  
  let errorMessage;
  if(errorBody?.errorInfo || (sessionStorage.getItem('headerType') && sessionStorage.getItem('headerType') === 'LOGIN')){
    errorMessage = 'Unable to load the data at the moment. Please try again later.';
    return errorMessage;
  } else {
    // if(status === 400 && 
    //   [
    //     'Bad request: general header fields missing',
    //     'Bad request: protected header fields found', 
    //     'Bad request: Requested output token type not found'
    //   ].indexOf(errorBody?.message) >= 0
    // ) {
    //   errorMessage = errorBody?.message
    // } else {
    //   errorMessage = 'Your session timed out, please re-login.'; // errorBody?.message
    // }
    errorMessage = 'Your session timed out, please re-login.';
    sessionStorage.setItem('loginError', errorMessage);
    sessionClear();
    window.location.replace(`${window.location.origin}/logout`);
    return errorMessage;
  }
}

const NO_USER_PROFILE_MSG = 'No account information found. Please contact your administrator.';

const redirectToLoginNoUserProfile = () => {
  sessionStorage.setItem('loginError', NO_USER_PROFILE_MSG);
  sessionClear();
  window.location.replace(`${window.location.origin}/loginchoice`);
  return { 'errorInfo': { 'code': 'ERR_api', 'causes': [NO_USER_PROFILE_MSG] } };
};

const handleResponse = async (response) => {
  let body;
  try {
    body = response.status === 204 ? {} : await parseJsonSafe(response);
  } catch (e) {
    body = { 'errorInfo': { 'code': 'ERR_api', 'causes': ['Unable to load the data at the moment. Please try again later.'] } }; // handle PAPI service down and the return value from gateway is not json data format
  }

  const isTechnicalError = `${response.status}`[0] === '5' ? true : false;
  
  const res = {
    errorInfo: body.errorInfo || body.error || body.message,
    status: response.status,
    isTechnicalError,
  };

  if (response.ok) {
    if (res.errorInfo) {
      return res;
    }
    return body;
  } else {
    // to catch and send the error response to front-end
    let causes;
    switch (response.status) {
    case 400:
      causes = 'Unable to load the data at the moment. Please try again later.';
      break;
    case 401:
      causes = validateErrorMessage(response.status,body);
      break;
    case 404:
      causes = 'Unable to load the data at the moment. Please try again later.';
      break;
    case 500:
      causes = 'Unable to load the data at the moment. Please try again later.';
      break;
    default:
      causes = 'Unable to load the data at the moment. Please try again later.';
    }
  // never change errorInfo format. Unless it will break down Notification Message of all the pages
  body = { 'errorInfo': { 'code': 'ERR_api', 'causes': [causes] } }; // handle PAPI service down and the return value from gateway is not json data format
  return body;
  }
};

export const overview = async payload => {
  let { overview: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeaders()
    })
  );
};

export const myCustomers = async payload => {
  let { myCustomers: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeaders()
    })
  );
};

export const myApplications = async payload => {
  let { myApplications: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeaders(),
    })
  );
};

export const policyInfo = async payload => {
  let { policyInfo: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeaders()
    })
  );
};

export const policyHolderDetails = async payload => {
  let { policyHolderDetails: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeaders()
    })
  );
};

export const policyDetails = async payload => {
  let { policyDetails: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeaders()
    })
  );
};

export const libraryDetails = async payload => {
  let { libraryDetails: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeaders()
    })
  );
};

export const myCampaigns = async payload => {
  let { myCampaigns: { url, method } } = API_CONFIG;
  const odsParams = await getOdsAgentParams();
  return await handleResponse(
    await fetch({
      url,
      method,
      payload: { ...payload, ...odsParams },
      headers: getHeaders()
    })
  );
};

export const campaignDocumentDownload = async payload => {
  let { campaignDocumentDownload: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeaders()
    })
  );
}

// global search api
export const myCustomersSearch = async payload => {
  let { myCustomersSearch: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeaders()
    })
  );
};

// download library document api
export const downloadDocuments = async payload => {
  let { downloadDocuments: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeaders()
    })
  );
};

// news api
export const newsDetails = async payload => {
  let { newsDetails: { url, method } } = API_CONFIG;
  const odsParams = await getOdsAgentParams();
  return await handleResponse(
    await fetch({
      url,
      method,
      payload: { ...payload, ...odsParams, ...(odsParams.products ? { products: odsParams.products.join(',') } : {}) },
      headers: getHeaders()
    })
  );
};

// agent api
// true: agent data is fetched from ODS directly by the frontend; false: from get_user_profile (admin portal)
const AGENT_DATA_FROM_ODS = true;

const agentDetailsFromUserProfile = async () => {
  let { agentDetails: { url, method } } = API_CONFIG;
  const response = await fetch({
    url,
    method,
    headers: { ...getHeaders(), 'x-hsbc-operation-code': sessionStorage.getItem('headerType') }
  });
  if (response.status === 404) {
    return redirectToLoginNoUserProfile();
  }
  return await handleResponse(response);
};

let odsAgentCache = {};

const agentDetailsFromOds = async () => {
  let { agentODSDetails: { url, method } } = API_CONFIG;
  const isStaff = isSsoSessionActive();
  const email = isStaff ? getSsoUserEmail() : getUserEmail();
  const response = await handleResponse(
    await fetch({
      url,
      method,
      payload: { ...(isStaff ? { filter: 'true' } : {}), email },
      headers: {
        ...getHeaders(),
        'X-HSBC-Channel-Id': 'OHI',
        'X-HSBC-Locale': 'en_SG',
        'Token_Type': 'SESSION_TOKEN'
      },
      skipSsoUserEmail: true
    })
  );
  if (response?.errorInfo) {
    return response;
  }
  const agent = selectAgent(toAgentList(response));
  if (!agent) {
    return redirectToLoginNoUserProfile();
  }
  const profile = mapOdsAgentToUserProfile(agent);
  odsAgentCache = { email, profile };
  return profile;
};

export const agentDetails = async () => (AGENT_DATA_FROM_ODS ? agentDetailsFromOds() : agentDetailsFromUserProfile());

// Agent filter params for news/campaigns, taken from ODS instead of the backend reading the admin portal profile.
// Returns {} on any failure so the backend keeps its existing admin portal lookup.
const getOdsAgentParams = async () => {
  if (!AGENT_DATA_FROM_ODS) {
    return {};
  }
  try {
    const email = isSsoSessionActive() ? getSsoUserEmail() : getUserEmail();
    const profile = odsAgentCache.email === email ? odsAgentCache.profile : await agentDetailsFromOds();
    const businessInfo = profile?.agentBusinessInfo;
    if (!businessInfo) {
      return {};
    }
    return {
      channel: businessInfo.channel || '',
      organization: businessInfo.organizationName || '',
      products: profile.agentProduct || [],
    };
  } catch (e) {
    return {};
  }
};

// Used by the ODS agent test page: same request as agentDetails, but returns the raw
// result instead of redirecting to login when no agent is found.
export const odsAgentLookup = async email => {
  let { agentODSDetails: { url, method } } = API_CONFIG;
  const response = await fetch({
    url,
    method,
    payload: { ...(isSsoSessionActive() ? { filter: 'true' } : {}), email },
    headers: {
      ...getHeaders(),
      'X-HSBC-Channel-Id': 'OHI',
      'X-HSBC-Locale': 'en_SG',
      'Token_Type': 'SESSION_TOKEN'
    },
    skipSsoUserEmail: true
  });
  const body = await parseJsonSafe(response);
  const agents = toAgentList(body);
  const agent = selectAgent(agents);
  return {
    status: response.status,
    agentCount: agents.length,
    profile: agent ? mapOdsAgentToUserProfile(agent) : null,
    body
  };
};

// notificationPref keys the admin portal record by email, so send the ODS agent email (the address notifications should go to) instead of the login email when it is known.
const getOdsAgentEmail = () => {
  const email = isSsoSessionActive() ? getSsoUserEmail() : getUserEmail();
  const odsEmail = odsAgentCache.email === email ? odsAgentCache.profile?.emailAddresses?.[0]?.email : '';
  return odsEmail ? String(odsEmail).trim() : '';
};

export const notificationPreference = async payload => {
  let { notificationPreference: { url, method } } = API_CONFIG;
  const odsEmail = getOdsAgentEmail();
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: { ...getHeaders(), 'x-hsbc-operation-code': sessionStorage.getItem('headerType'), ...(odsEmail ? { 'X-HSBC-User-Email': odsEmail } : {}) },
      skipSsoUserEmail: !!odsEmail
    })
  );
};

// ODS holds the agent profile but not the notification preferences, so those are still read from the user profile service.
export const notificationPreferenceDetails = async () => {
  let { notificationPreferenceDetails: { url, method } } = API_CONFIG;
  const odsEmail = getOdsAgentEmail();
  return await handleResponse(
    await fetch({
      url,
      method,
      headers: { ...getHeaders(), 'x-hsbc-operation-code': sessionStorage.getItem('headerType'), ...(odsEmail ? { 'X-HSBC-User-Email': odsEmail } : {}) },
      skipSsoUserEmail: !!odsEmail
    })
  );
};

export const authCodeDetail = async payload => {
  let { authCodeDetail: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeadersForAuthCode(),
      skipCidToken: true
    })
  );
}

// news document download api
export const newsDocumentDownload = async payload => {
  let { newsDocumentDownload: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeaders()
    })
  );
}

export const getAccessToken = async payload => {
  let { getAccessToken: { url, method } } = API_CONFIG;
  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: { ...getHeaders(), 'Content-Type': "application/x-www-form-urlencoded" },
      skipCidToken: true
    })
  );
};
/**
*@description call postSales illustration 
*/
export const postSalesIllustration = async payload => {
  const postSalesConfig = API_CONFIG?.postSalesIllustration || {};
  let {postSalesIllustration: {url, method}} = API_CONFIG;  
  const email = getUserEmail()?getUserEmail().trim() : getSsoUserEmail()?.trim();
  payload = { ...payload
    , "identifier": 
    {"type": "emailAddress","value":email}
  };

  return await handleResponse(
    await fetch({
      url,
      method,
      payload,
      headers: getHeaders(),
    })
  );
}